import React, { useEffect, useRef, useState } from "react";
import { applyAiI18n, stripViewportUnits } from "./aiI18n";
import { getLayoutDirection } from "../locales";

/**
 * AiHtmlIframe — composant partag\u00e9 pour rendre un HTML g\u00e9n\u00e9r\u00e9 par l'IA
 * dans une iframe isol\u00e9e avec auto-resize, traduction et gestion des liens.
 *
 * Utilis\u00e9 par :
 * - AiContentSlot (blocs IA r\u00e9utilisables)
 * - Travel/CustomPage (pages CMS plein \u00e9cran)
 * - Ummah/CustomPage (pages CMS plein \u00e9cran)
 *
 * Props :
 * - html (string)         : le HTML \u00e0 afficher (fragment ou doc complet)
 * - i18n (object)         : { [lang]: { [key]: value } } — dict de traductions
 * - mainLang (string)     : langue principale du contenu (d\u00e9faut "fr")
 * - lang (string|object)  : langue cible. Accepte un string ou {value: "..."}
 * - linkBehavior          : "top" (navigue le top frame) | "blank" (nouvel onglet)
 * - className, style      : pour styliser le wrapper
 */
export default function AiHtmlIframe({
    html,
    i18n,
    mainLang = "fr",
    lang,
    linkBehavior = "top",
    className,
    style,
}) {
    const iframeRef = useRef(null);
    const [height, setHeight] = useState(0);
    // ID unique et STABLE par instance : sans lui, plusieurs AiHtmlIframe sur
    // la même page reçoivent tous le même message global "message" et
    // appliquent la hauteur d'une AUTRE iframe → hauteur fausse (trop grande/
    // petite) une fois sur deux selon l'ordre des mesures. Le srcdoc renvoie
    // cet id, le handler ne garde que les messages destinés à SON iframe.
    const frameIdRef = useRef(null);
    if (frameIdRef.current === null) {
        frameIdRef.current = "aicb_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }

    // Normalise la langue : accepte string ou {value: string}
    const langCode = (lang && typeof lang === "object") ? lang.value : lang;

    // Re-injecte le contenu \u00e0 chaque changement
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !html) return;

        const targetLang = langCode || mainLang;
        // Direction du contenu DANS l'iframe : le dir="rtl" de la page parente ne
        // traverse pas la frontière de l'iframe → sans ça, un contenu hébreu reste
        // rendu en LTR. On réutilise le helper central du système web (locales).
        const dir = getLayoutDirection(targetLang);
        const dict = i18n?.[targetLang];
        const fallback = i18n?.[mainLang];

        const translatedHtml = (targetLang === mainLang)
            ? html
            : applyAiI18n(html, dict, fallback);

        const finalHtml = stripViewportUnits(translatedHtml);

        // Script enfant : auto-resize + gestion des liens selon linkBehavior
        const childScript = `
<script>
  (function() {
    var lastHeight = 0;
    var rafId = null;

    // Mesure robuste : getBoundingClientRect().bottom du dernier enfant +
    // scrollHeight, ce qui capture les marges (margin collapsing) que
    // body.scrollHeight ignore. On prend le max de toutes les sources.
    function measure() {
      var de = document.documentElement;
      var body = document.body;
      var h = Math.max(
        body.scrollHeight, body.offsetHeight,
        de.scrollHeight, de.offsetHeight
      );
      // Capture la marge basse du dernier enfant (souvent oubliée).
      var last = body.lastElementChild;
      if (last) {
        var rect = last.getBoundingClientRect();
        var mb = parseFloat(getComputedStyle(last).marginBottom) || 0;
        h = Math.max(h, Math.ceil(rect.bottom + mb));
      }
      return h;
    }

    var FRAME_ID = ${JSON.stringify(frameIdRef.current)};
    function resize() {
      var h = measure();
      if (Math.abs(h - lastHeight) < 2) return;
      lastHeight = h;
      parent.postMessage({ __aiHtmlIframeHeight: true, id: FRAME_ID, height: h }, '*');
    }

    // Double déclenchement : rAF (debounce quand l'iframe est peinte) +
    // setTimeout de secours (rAF est GELÉ quand l'iframe n'est pas visible
    // / onglet en arrière-plan ; sans ce fallback, aucune mesure ne part).
    var toId = null;
    function scheduleResize() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(resize);
      if (toId) clearTimeout(toId);
      toId = setTimeout(resize, 0);
    }

    window.addEventListener('load', scheduleResize);
    window.addEventListener('resize', scheduleResize);

    // ResizeObserver sur <html> ET <body> : <html> bouge même quand le
    // body garde sa taille mais qu'un enfant déborde.
    var observer = new ResizeObserver(scheduleResize);
    observer.observe(document.documentElement);
    observer.observe(document.body);

    // MutationObserver : rattrape les injections DOM tardives (ex.
    // lucide.createIcons() qui remplace <i> par <svg>, contenu différé…).
    var mo = new MutationObserver(scheduleResize);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Re-mesure quand les polices web sont prêtes (clamp()/line-height
    // changent la hauteur une fois la vraie font appliquée).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleResize);
    }

    // Re-mesure à la fin du chargement de chaque image / média.
    function watchMedia() {
      var medias = document.querySelectorAll('img, iframe, video');
      for (var i = 0; i < medias.length; i++) {
        var m = medias[i];
        if (m.complete) continue;
        m.addEventListener('load', scheduleResize, { once: true });
        m.addEventListener('error', scheduleResize, { once: true });
      }
    }
    watchMedia();

    // Filet de sécurité : quelques mesures différées pour les scripts
    // tiers lents (unpkg…) que les observers pourraient manquer.
    setTimeout(scheduleResize, 100);
    setTimeout(scheduleResize, 500);
    setTimeout(function(){ watchMedia(); scheduleResize(); }, 1200);
    setTimeout(scheduleResize, 2500);

    // Gestion des liens selon linkBehavior
    var linkBehavior = ${JSON.stringify(linkBehavior)};
    document.addEventListener('click', function(e) {
      var a = e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href) return;
      if (href.startsWith('#') || href.startsWith('javascript:')) return;

      if (linkBehavior === 'blank') {
        if (!a.target) { a.target = '_blank'; }
      } else {
        e.preventDefault();
        try { window.top.location.href = a.href; }
        catch (err) { window.open(a.href, '_blank'); }
      }
    }, true);

    scheduleResize();
  })();
<\/script>`;

        iframe.srcdoc = `<!DOCTYPE html>
<html lang="${targetLang}" dir="${dir}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<base target="_top">
<style>
  html, body { margin: 0; padding: 0; background: transparent; overflow: hidden; }
  html, body { height: auto !important; min-height: 0 !important; }
  /* Suit la direction du document (rtl pour l'hébreu) sans forcer une valeur. */
  body { text-align: start; }
</style>
</head>
<body>
<div dir="${dir}">
${finalHtml}
</div>
${childScript}
</body>
</html>`;
    }, [html, langCode, i18n, mainLang, linkBehavior]);

    // \u00c9coute les messages de redimensionnement
    useEffect(() => {
        const handler = (e) => {
            if (
                e.data &&
                e.data.__aiHtmlIframeHeight &&
                e.data.id === frameIdRef.current &&   // n'accepte que SON iframe
                typeof e.data.height === "number"
            ) {
                setHeight(e.data.height);
            }
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, []);

    if (!html) return null;

    return (
        <div className={className} style={{ width: "100%", ...style }}>
            <iframe
                ref={iframeRef}
                title="ai-html"
                scrolling="no"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
                style={{
                    width: "100%",
                    height: `${height}px`,
                    border: "none",
                    display: "block",
                    background: "transparent",
                }}
            />
        </div>
    );
}
